/**
 * PWA Update Store - Manages service worker update state
 *
 * Follows the same listener/subscribe pattern as toast-store.
 * When a new service worker is waiting, `needsRefresh` becomes true
 * and the UI can show a non-blocking prompt. The user (or a
 * navigation-based auto-apply) calls `applyUpdate()` when ready.
 */

type PwaUpdateListener = (needsRefresh: boolean) => void

class PwaUpdateStore {
  private _needsRefresh = false
  private listeners = new Set<PwaUpdateListener>()
  private _applyUpdate: (() => Promise<void>) | undefined

  /** Whether a new version is waiting to activate */
  get needsRefresh(): boolean {
    return this._needsRefresh
  }

  /** Subscribe to update-available changes */
  subscribe(listener: PwaUpdateListener): () => void {
    this.listeners.add(listener)
    listener(this._needsRefresh)
    return () => this.listeners.delete(listener)
  }

  /**
   * Called by main.tsx when the service worker reports a new version.
   * `acceptCb` should invoke `updateSW(true)` which tells the waiting
   * SW to activate and then reloads the page.
   */
  setNeedsRefresh(acceptCb: () => Promise<void>): void {
    this._applyUpdate = acceptCb
    this._needsRefresh = true
    this.emit()
  }

  /** User/app chose to reload now */
  applyUpdate(): void {
    if (this._applyUpdate) {
      this._applyUpdate()
    }
  }

  private emit(): void {
    for (const listener of this.listeners) {
      listener(this._needsRefresh)
    }
  }
}

export const pwaUpdateStore = new PwaUpdateStore()
